/**
 * Email-preview store (trial mode, 04 §4.8). In trial orgs the per-codebase
 * email guard sends nothing — it persists a preview doc at
 * orgs/{orgId}/emailPreviews/{id} and returns `{ preview: true, previewId }`.
 * This tiny external store (same pattern as paletteStore/ledgerStore) lets any
 * send call site surface that preview through the single EmailPreviewModal
 * mounted in the app shell, without prop-drilling.
 */
import { useSyncExternalStore } from "react";

let state = { open: false, previewId: null, orgId: null, preview: null };
const listeners = new Set();

function emit() {
  state = { ...state };
  listeners.forEach((l) => l());
}

/**
 * Open the preview modal. Pass either a resolved preview object
 * (`{ html, subject, to, emailType }`) or a `{ previewId, orgId }` the modal
 * fetches from Firestore.
 */
export function showEmailPreview({ previewId = null, orgId = null, preview = null } = {}) {
  state = { open: true, previewId, orgId, preview };
  emit();
}

export function closeEmailPreview() {
  state = { ...state, open: false };
  emit();
}

/**
 * Shared send-result handler: the canonical hook every suppressible send call
 * site can adopt. When a callable response carries the trial preview envelope
 * (`{ preview: true, previewId }`), open the modal instead of claiming an email
 * was sent. Returns true when it handled a preview, false otherwise (so callers
 * can fall through to their normal "email sent" UX).
 *
 * TODO(trial): adopt at the existing suppressible send call sites (report
 * shares, donor statements, etc.) as they are threaded with orgId/emailType.
 */
// TODO(trial-followup): adopt at the sync send sites (sendInvoice /
// sendContactStatement / sendBatchNotification UIs) — pending follow-up.
export function maybeShowEmailPreview(result, orgId) {
  const data = result?.data ?? result;
  if (data && data.preview) {
    showEmailPreview({ previewId: data.previewId, orgId });
    return true;
  }
  return false;
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useEmailPreview() {
  return useSyncExternalStore(subscribe, () => state);
}
